import type { EffectCleanupRegisterFn } from '@angular/core';
import type MapView from '@arcgis/core/views/MapView';
import type { ArcgisMap } from '@arcgis/map-components/components/arcgis-map';

import { watch } from '@arcgis/core/core/reactiveUtils.js';

import type { Map2 } from './map-2';

import { GeoFromExtent } from './utils/geoFromExtent';

// Keeps the filtering service in sync with the current map viewport so listings (sidebar, no-results reason,
// shareable URL) can be scoped to what's actually visible on the map.
export function WatchForExtentChanges(this: Map2, onCleanup: EffectCleanupRegisterFn): void {
  if (!this.stateService.mapViewCreated()) {
    return;
  }

  const _view = (this.mapElement()?.nativeElement as ArcgisMap).view;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- The view may not be available yet if the map is still initializing.
  if (!_view) {
    return;
  }

  this.mapView = _view;

  // `stationary` only flips once panning/zooming settles, so this stays cheap even during active gestures.
  const _handle = watch(
    () => _view.stationary,
    (stationary) => {
      if (!stationary) {
        return;
      }

      updateExtentFilter.call(this, _view);
    },
  );

  onCleanup(() => {
    _handle.remove();
  });
}

function updateExtentFilter(this: Map2, view: MapView): void {
  // -1 means the view doesn't have a resolved zoom level yet (e.g. still initializing).
  if (view.zoom === -1) {
    return;
  }

  const _extent = view.extent;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- The extent may not be available yet if the map is still initializing.
  if (!_extent) {
    return;
  }

  const _geo = GeoFromExtent(_extent);

  // Avoid redundant writes so downstream computeds (sidebar listings, URL params) don't recompute needlessly.
  if (this.filteringService.geoWithin().toString() !== _geo.toString()) {
    this.filteringService.geoWithin.set(_geo);
  }

  if (this.filteringService.lvl() !== view.zoom) {
    this.filteringService.lvl.set(view.zoom);
  }

  const _latitude = view.center.latitude;
  const _longitude = view.center.longitude;

  if (!_latitude || !_longitude) {
    return;
  }

  if (this.filteringService.lat() !== _latitude || this.filteringService.lng() !== _longitude) {
    this.filteringService.lat.set(_latitude);
    this.filteringService.lng.set(_longitude);
  }
}
